import { normalize } from 'bundles/search-common/utils/searchCards';

import _t from 'i18n!nls/search-common';

import ibmHero from './assets/ibm-hero.png';
import ibmLogoWhite from './assets/ibm-logo-white.svg';

type TakeoverPilotConfigCommon = {
  campaignId: string;
  partnerId: string;
  partnerName: string;
  triggerQueries: string[];
  ctaLabel: string;
  backgroundColor?: string;
  heroImageUrl?: string;
  heroImageAlt?: string;
  heroImageIsFullBleed?: boolean;
  partnerLogoUrl?: string;
};

export type TakeoverPilotConfigEntry = TakeoverPilotConfigCommon &
  (
    | {
        mode: 'match';
        targetSlug: string;
        targetPath: string;
      }
    | {
        mode: 'insert';
        targetPath: string;
        headline: string; // TODO: placeholder copy, replace with approved marketing copy.
        description?: string;
      }
  );

const dedupeNormalized = (queries: string[]): string[] => Array.from(new Set(queries.map(normalize)));

// IBM-only for now, so every campaign shares the same creative.
const IBM_HERO_CREATIVE = {
  heroImageUrl: ibmHero,
  heroImageAlt: 'IBM',
  heroImageIsFullBleed: true,
  partnerLogoUrl: ibmLogoWhite,
};

// Called lazily (never at module scope) so _t() below always runs with the request's locale.
const getCampaignEntries = (): TakeoverPilotConfigEntry[] => [
  {
    campaignId: 'IBM_ONLINE_COURSES',
    partnerId: 'ibm',
    partnerName: 'IBM',
    triggerQueries: dedupeNormalized(['IBM', 'ibm', 'Ibm']),
    mode: 'insert',
    targetPath: '/explore/ibm-online-courses',
    headline: _t('Learn in-demand skills with IBM'),
    description: _t('Explore IBM courses on Coursera and build job-ready skills.'),
    ctaLabel: _t('Explore IBM courses'),
    ...IBM_HERO_CREATIVE,
  },
  {
    campaignId: 'IBM_DATA_SCIENCE',
    partnerId: 'ibm',
    partnerName: 'IBM',
    triggerQueries: dedupeNormalized([
      'python',
      'Python for beginners',
      'Python',
      'python data science',
      'ibm python',
      'python for data science',
      'python programming',
      'data science',
      'Data Science',
      'Data Science Fundamentals',
      'Data Science Specialization',
      'data science specialization',
      'sql for data science',
      'ibm data science',
    ]),
    mode: 'match',
    targetSlug: 'ibm-data-science',
    targetPath: '/professional-certificates/ibm-data-science',
    ctaLabel: _t('Explore the certificate'),
    ...IBM_HERO_CREATIVE,
  },
  {
    campaignId: 'IBM_GENERATIVE_AI_ENGINEERING',
    partnerId: 'ibm',
    partnerName: 'IBM',
    triggerQueries: dedupeNormalized([
      'artificial intelligence',
      'Artificial Intelligence',
      'ai',
      'AI',
      'generative ai',
      'ibm ai',
      'ai literacy',
      'Generative AI',
      'gen ai',
      'ai essentials',
    ]),
    mode: 'match',
    targetSlug: 'ibm-generative-ai-engineering',
    targetPath: '/professional-certificates/ibm-generative-ai-engineering',
    ctaLabel: _t('Explore the certificate'),
    ...IBM_HERO_CREATIVE,
  },
  {
    campaignId: 'IBM_DATA_ANALYST',
    partnerId: 'ibm',
    partnerName: 'IBM',
    triggerQueries: dedupeNormalized([
      'data analytics',
      'Data Analytics',
      'Data analytics',
      'ibm data analytics',
      'sql for data analytics',
      'python for data analytics',
      'python data analytics',
      'data analytics ibm',
    ]),
    mode: 'match',
    targetSlug: 'ibm-data-analyst',
    targetPath: '/professional-certificates/ibm-data-analyst',
    ctaLabel: _t('Explore the certificate'),
    ...IBM_HERO_CREATIVE,
  },
];

// Campaign data keyed by campaignId. Called lazily for the same reason as getCampaignEntries.
export const getCampaignConfig = (): Record<string, TakeoverPilotConfigEntry> =>
  Object.fromEntries(getCampaignEntries().map((entry) => [entry.campaignId, entry]));

// Reverse index: normalized trigger query -> campaignId, for O(1) query lookups.
export const getQueryToCampaign = (): Record<string, string> =>
  Object.fromEntries(
    getCampaignEntries().flatMap((entry) =>
      entry.triggerQueries.map((triggerQuery) => [triggerQuery, entry.campaignId])
    )
  );
